import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import { describeExpense } from '../../utils/loanExpenseUtils';
import { toCurrency } from '../../utils/adminMetrics';
import ResponsiveTable from '../ResponsiveTable';

/**
 * Pick which additional expenses a loan carries.
 *
 * `value` is a list of expense codes: this only chooses which expenses the
 * loan carries. The amounts come from the API's loan quote
 * (GET /api/loans/quote), passed in as `priced` and `total`, so the screen
 * shows exactly what the loan will store.
 *
 * @param {string[]} value         chosen expense codes
 * @param {Function} onChange      called with the next list of codes
 * @param {object[]} priced        the quote's `expenses` rows (null until quoted)
 * @param {number}   total         the quote's `expenses_total` (null until quoted)
 * @param {boolean}  readOnly      show the list without add/remove
 * @param {Function} onTypesLoaded receives the active expense types once fetched
 */
const LoanExpensesEditor = ({
  value = [],
  onChange,
  priced = null,
  total = null,
  readOnly = false,
  onTypesLoaded,
  className = '',
}) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toAdd, setToAdd] = useState('');
  // A new expense type, created here and added to the loan in one step.
  const { user } = useContext(AuthContext) || {};
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const canCreateType = perms.includes('*') || perms.includes('ManageConfig');
  const [creating, setCreating] = useState(false);
  const [newType, setNewType] = useState({ name: '', method: 'fixed', amount: '' });
  const [createError, setCreateError] = useState('');
  const [savingType, setSavingType] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-expense-types'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data.filter((t) => t.is_active !== false) : [];
        setTypes(list);
        if (typeof onTypesLoaded === 'function') onTypesLoaded(list);
      } catch {
        if (alive) setError('Could not load the expense types.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  // One row per chosen code, with the quote's amount once it is in.
  const rows = useMemo(() => {
    const pricedByCode = new Map((priced || []).map((r) => [String(r.expense_code).toUpperCase(), r]));
    const typeByCode = new Map(types.map((t) => [String(t.expense_code).toUpperCase(), t]));
    return (value || [])
      .map((code) => String(code).toUpperCase())
      .filter((code) => typeByCode.has(code) || pricedByCode.has(code))
      .map((code) => {
        const row = pricedByCode.get(code);
        const type = typeByCode.get(code);
        return {
          expense_code: row?.expense_code || type?.expense_code || code,
          expense_name: row?.expense_name || type?.expense_name || code,
          calculated_amount: row ? row.calculated_amount : null,
        };
      });
  }, [types, value, priced]);
  const money = (n) => (n === null || n === undefined ? '—' : toCurrency(n));

  const chosen = useMemo(
    () => new Set((value || []).map((c) => String(c).toUpperCase())),
    [value]
  );
  const available = types.filter((t) => !chosen.has(String(t.expense_code).toUpperCase()));

  const remove = (code) => {
    if (readOnly || typeof onChange !== 'function') return;
    onChange((value || []).filter((c) => String(c).toUpperCase() !== String(code).toUpperCase()));
  };

  const add = () => {
    if (readOnly || !toAdd || typeof onChange !== 'function') return;
    if (chosen.has(String(toAdd).toUpperCase())) return;
    onChange([...(value || []), toAdd]);
    setToAdd('');
  };

  const createAndAdd = async () => {
    const name = newType.name.trim();
    const amount = parseFloat(newType.amount);
    if (!name) { setCreateError('Give the expense a name.'); return; }
    if (!(amount >= 0)) { setCreateError(newType.method === 'fixed' ? 'Enter the amount.' : 'Enter the percentage.'); return; }
    const base = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'EXPENSE';
    const taken = new Set(types.map((t) => String(t.expense_code).toUpperCase()));
    let code = base;
    for (let i = 2; taken.has(code); i += 1) code = `${base}_${i}`;
    setSavingType(true);
    setCreateError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(apiUrl('/api/loan-expense-types'), {
        expense_code: code,
        expense_name: name,
        calculation_method: newType.method,
        default_rate_percent: newType.method === 'percentage' ? amount : undefined,
        default_fixed_amount: newType.method === 'fixed' ? amount : undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const created = res.data || {};
      setTypes((list) => [...list, created]);
      if (typeof onChange === 'function') onChange([...(value || []), created.expense_code || code]);
      setNewType({ name: '', method: 'fixed', amount: '' });
      setCreating(false);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'The expense could not be created.');
    } finally {
      setSavingType(false);
    }
  };

  return (
    <div className={`rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 ${className}`}>
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Additional Expenses</h4>
        <span className="text-xs text-gray-400 dark:text-slate-500">added to the amount repayable</span>
        <span className="ml-auto text-sm font-semibold text-gray-900 dark:text-slate-100">
          {money(total)}
        </span>
      </div>

      <div className="p-4 space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 py-3">
            <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <span className="text-xs text-gray-500 dark:text-slate-400">Loading expense types…</span>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400 py-1">
            No additional expenses on this loan.
          </p>
        ) : (
          <ResponsiveTable
            data={rows}
            keyField="expense_code"
            columns={[
              { field: 'expense_name', header: 'Expense' },
              {
                field: 'expense_code',
                header: 'How it is charged',
                formatter: (code) => describeExpense(types.find((t) => String(t.expense_code).toUpperCase() === String(code).toUpperCase())),
              },
              { field: 'calculated_amount', header: 'Amount', align: 'right', formatter: (v) => money(v) },
              ...(readOnly ? [] : [{
                field: 'remove',
                header: '',
                formatter: (_, row) => (
                  <button
                    type="button"
                    onClick={() => remove(row.expense_code)}
                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors"
                  >
                    Remove
                  </button>
                ),
              }]),
            ]}
          />
        )}

        {!readOnly && !loading && !error && (
          available.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <select
                value={toAdd}
                onChange={(e) => setToAdd(e.target.value)}
                aria-label="Expense to add"
                className="flex-1 min-w-[160px]"
              >
                <option value="">Add an expense…</option>
                {available.map((t) => (
                  <option key={t.expense_code} value={t.expense_code}>
                    {t.expense_name} — {describeExpense(t)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={add}
                disabled={!toAdd}
                className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
              >
                Add
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 dark:text-slate-500 pt-1">
              {types.length === 0
                ? 'No expense types are configured yet.'
                : 'Every configured expense type is already on this loan.'}
            </p>
          )
        )}

        {!readOnly && !loading && !error && (
          canCreateType ? (
            creating ? (
              <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50/60 dark:bg-blue-900/20 p-3 space-y-2">
                <p className="text-xs font-semibold text-blue-900 dark:text-blue-200">New expense</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    value={newType.name}
                    onChange={(e) => setNewType((t) => ({ ...t, name: e.target.value }))}
                    placeholder="Name, e.g. Admin fee"
                    aria-label="Expense name"
                    className="sm:col-span-3"
                  />
                  <select
                    value={newType.method}
                    onChange={(e) => setNewType((t) => ({ ...t, method: e.target.value }))}
                    aria-label="How it is charged"
                  >
                    <option value="fixed">Fixed amount (N$)</option>
                    <option value="percentage">% of principal</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newType.amount}
                    onChange={(e) => setNewType((t) => ({ ...t, amount: e.target.value }))}
                    placeholder={newType.method === 'fixed' ? 'Amount (N$)' : 'Percent'}
                    aria-label={newType.method === 'fixed' ? 'Amount' : 'Percentage'}
                  />
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={createAndAdd} disabled={savingType}
                      className="flex-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold">
                      {savingType ? 'Adding…' : 'Add'}
                    </button>
                    <button type="button" onClick={() => { setCreating(false); setCreateError(''); }}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700">
                      Cancel
                    </button>
                  </div>
                </div>
                {createError && <p className="text-[11px] text-rose-600 dark:text-rose-400">{createError}</p>}
                <p className="text-[11px] text-gray-500 dark:text-slate-400">
                  It is also saved under Loan Expenses, so it can be used on other loans.
                </p>
              </div>
            ) : (
              <button type="button" onClick={() => setCreating(true)}
                className="text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                + New expense
              </button>
            )
          ) : null
        )}
      </div>
    </div>
  );
};

export default LoanExpensesEditor;
